/**
 * Shared API error parser for generation adapters.
 *
 * Handles two response shapes produced by the backend:
 *   Format 1 (flat):   { error_code, error, setup_instructions }
 *   Format 2 (nested): { error: { code, message, details: { user_message, setup_instructions } } }
 *
 * setup_instructions is always a string (canonical per exceptions/base_exceptions.py).
 */

interface ParsedApiError {
  error: string;
  error_code?: string;
  setup_instructions?: string;
}

interface Format1Data {
  error_code?: string;
  error?: string;
  setup_instructions?: string;
}

interface Format2NestedError {
  code?: string;
  message?: string;
  details?: {
    user_message?: string;
    setup_instructions?: string;
  };
}

interface Format2Data {
  error?: Format2NestedError | string;
}

type ResponseData = Format1Data & Format2Data;

interface AxiosLike {
  response?: {
    data?: ResponseData;
    status?: number;
  };
  message?: string;
}

/**
 * Parse an axios error into a normalised error object.
 * Returns null if the error is not a structured API error response
 * (caller should then throw the raw error).
 */
export function parseApiError(axiosError: AxiosLike): ParsedApiError | null {
  const responseData = axiosError.response?.data;
  if (!responseData) {
    return null;
  }

  // Format 1: flat error_code at the top level
  if (responseData.error_code) {
    return {
      error: (responseData as Format1Data).error ?? 'Configuration error',
      error_code: responseData.error_code,
      setup_instructions: (responseData as Format1Data).setup_instructions ?? '',
    };
  }

  // Format 2: nested error object
  const nestedError: Format2NestedError | string | undefined = responseData.error;
  if (typeof nestedError === 'object' && nestedError !== null && nestedError.code) {
    return {
      error: nestedError.details?.user_message ?? nestedError.message ?? 'Configuration error',
      error_code: nestedError.code,
      setup_instructions: nestedError.details?.setup_instructions ?? '',
    };
  }

  return null;
}
